import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Wallet,
  Heart,
  Briefcase,
  Code,
  Globe,
  GraduationCap,
  Trophy,
  Music,
  Camera,
  BookOpen,
  Star,
  Plus,
  Pencil,
  Trash2,
  ImageIcon,
  Calendar,
  Sparkles,
  X,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Users,
  Wallet,
  Heart,
  Briefcase,
  Code,
  Globe,
  GraduationCap,
  Trophy,
  Music,
  Camera,
  BookOpen,
  Star,
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

// Curated fallback covers per icon category (Unsplash, free to use)
const FALLBACK_COVERS: Record<string, string> = {
  Users: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1024&q=80&auto=format&fit=crop",
  GraduationCap: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1024&q=80&auto=format&fit=crop",
  Wallet: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1024&q=80&auto=format&fit=crop",
  Heart: "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1024&q=80&auto=format&fit=crop",
  Briefcase: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1024&q=80&auto=format&fit=crop",
  Code: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1024&q=80&auto=format&fit=crop",
  Globe: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1024&q=80&auto=format&fit=crop",
  Trophy: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=1024&q=80&auto=format&fit=crop",
  Music: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1024&q=80&auto=format&fit=crop",
  Camera: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1024&q=80&auto=format&fit=crop",
  BookOpen: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1024&q=80&auto=format&fit=crop",
  Star: "https://images.unsplash.com/photo-1532012197267-da84d127e765?w=1024&q=80&auto=format&fit=crop",
};

interface OrgActivity {
  id: string;
  title: string;
  organization: string;
  role: string;
  dates: string;
  description: string;
  icon_name: string;
  image_url: string | null;
  sort_order: number;
}

const extractYear = (dates: string) => {
  const match = dates.match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : "";
};

const isActive = (dates: string) => dates.toLowerCase().includes("present");

const Organizational = () => {
  const { t } = useLanguage();
  const { isAdmin, user } = useAuth();
  const [activities, setActivities] = useState<OrgActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState<OrgActivity | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [organization, setOrganization] = useState("");
  const [role, setRole] = useState("");
  const [dates, setDates] = useState("");
  const [description, setDescription] = useState("");
  const [iconName, setIconName] = useState("Users");
  const [sortOrder, setSortOrder] = useState("0");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);

  const fetchActivities = async () => {
    const { data, error } = await supabase
      .from("organizational_activities")
      .select("*")
      .order("sort_order", { ascending: true });

    if (!error && data) {
      setActivities(data as OrgActivity[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const resetForm = () => {
    setTitle("");
    setOrganization("");
    setRole("");
    setDates("");
    setDescription("");
    setIconName("Users");
    setSortOrder(String(activities.length));
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    setSelected(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (a: OrgActivity) => {
    setSelected(a);
    setTitle(a.title);
    setOrganization(a.organization);
    setRole(a.role);
    setDates(a.dates);
    setDescription(a.description);
    setIconName(a.icon_name || "Users");
    setSortOrder(String(a.sort_order));
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(a.image_url);
    setDialogOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return existingImageUrl;
    const ext = imageFile.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage
      .from("organizational-images")
      .upload(fileName, imageFile);
    if (error) {
      toast.error(`Image upload failed: ${error.message}`);
      return null;
    }
    const { data } = supabase.storage
      .from("organizational-images")
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const deleteImageFromStorage = async (url: string | null) => {
    if (!url) return;
    const path = url.split("/organizational-images/")[1];
    if (path) {
      await supabase.storage.from("organizational-images").remove([path]);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !organization.trim() || !role.trim() || !dates.trim()) {
      toast.error("Title, organization, role, and dates are required");
      return;
    }
    setSaving(true);

    let imageUrl: string | null = existingImageUrl;
    if (imageFile) {
      // remove old if replacing
      if (selected?.image_url && selected.image_url !== existingImageUrl) {
        await deleteImageFromStorage(selected.image_url);
      }
      imageUrl = await uploadImage();
      if (!imageUrl) {
        setSaving(false);
        return;
      }
    } else if (selected?.image_url && !existingImageUrl) {
      // user removed image
      await deleteImageFromStorage(selected.image_url);
      imageUrl = null;
    }

    const payload = {
      title: title.trim(),
      organization: organization.trim(),
      role: role.trim(),
      dates: dates.trim(),
      description: description.trim(),
      icon_name: iconName,
      sort_order: parseInt(sortOrder) || 0,
      image_url: imageUrl,
    };

    let error;
    if (selected) {
      ({ error } = await supabase
        .from("organizational_activities")
        .update(payload)
        .eq("id", selected.id));
    } else {
      ({ error } = await supabase
        .from("organizational_activities")
        .insert({ ...payload, created_by: user?.id }));
    }

    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(selected ? "Activity updated" : "Activity added");
    setDialogOpen(false);
    resetForm();
    fetchActivities();
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (selected.image_url) {
      await deleteImageFromStorage(selected.image_url);
    }
    const { error } = await supabase
      .from("organizational_activities")
      .delete()
      .eq("id", selected.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Activity deleted");
    setDeleteDialogOpen(false);
    setSelected(null);
    fetchActivities();
  };

  // Fallback to locale data if DB is empty
  const displayActivities: OrgActivity[] =
    activities.length > 0
      ? activities
      : t.organizational.activities.map((a: { title: string; organization: string; role: string; dates: string; description: string }, idx: number) => ({
          id: `local-${idx}`,
          title: a.title,
          organization: a.organization,
          role: a.role,
          dates: a.dates,
          description: a.description,
          icon_name: ["Users", "Wallet", "Heart"][idx] || "Users",
          image_url: null,
          sort_order: idx,
        }));

  return (
    <section
      id="organizational"
      className="py-16 sm:py-20 md:py-28 relative overflow-hidden"
    >
      {/* Decorative background blobs */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-primary/10 blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 -right-20 w-96 h-96 rounded-full bg-accent/10 blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 animate-fade-up">
          <div className="inline-flex items-center gap-2 mb-4 text-xs font-semibold tracking-[0.2em] uppercase text-primary">
            <span className="h-px w-8 bg-primary/50" />
            <Sparkles className="h-3.5 w-3.5" />
            <span>Community</span>
            <Sparkles className="h-3.5 w-3.5" />
            <span className="h-px w-8 bg-primary/50" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight pb-1">
            {t.organizational.title}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t.organizational.subtitle}
          </p>
          {isAdmin && (
            <Button
              onClick={openCreate}
              className="mt-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground shadow-medium"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Activity
            </Button>
          )}
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {displayActivities.map((activity, index) => {
            const Icon = ICON_MAP[activity.icon_name] || Users;
            const year = extractYear(activity.dates);
            const active = isActive(activity.dates);
            const isLocal = activity.id.startsWith("local-");

            return (
              <Card
                key={activity.id}
                className="group relative overflow-hidden border-border/50 bg-card/80 backdrop-blur-sm shadow-soft hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:-translate-y-2 animate-fade-up"
                style={{ animationDelay: `${index * 120}ms` }}
              >
                {/* Cover area. The outer wrapper is deliberately unclipped so the icon
                    badge can overhang into the content area; only the image box clips, which
                    is what the hover zoom needs. */}
                <div className="relative">
                  <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={activity.image_url || FALLBACK_COVERS[activity.icon_name] || FALLBACK_COVERS.Users}
                    alt={activity.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Gradient overlays for legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-accent/15" />

                  {/* Year badge */}
                  {year && (
                    <div className="absolute top-3 left-3 z-10">
                      <Badge className="bg-background/80 backdrop-blur-md text-foreground border border-border/50 font-mono text-xs px-2.5 py-1 shadow-soft">
                        Since {year}
                      </Badge>
                    </div>
                  )}

                  {/* Active status */}
                  {active && (
                    <div className="absolute top-3 right-3 z-10">
                      <Badge className="bg-emerald-500/20 backdrop-blur-md text-emerald-400 border border-emerald-500/30 text-xs px-2.5 py-1 shadow-soft">
                        <span className="relative flex h-2 w-2 mr-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                        </span>
                        Active
                      </Badge>
                    </div>
                  )}

                  {/* Admin controls */}
                  {isAdmin && !isLocal && (
                    <div className="absolute bottom-3 right-3 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8 backdrop-blur-md bg-background/80 hover:bg-background shadow-soft"
                        onClick={() => openEdit(activity)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8 backdrop-blur-md bg-background/80 hover:bg-destructive hover:text-destructive-foreground shadow-soft"
                        onClick={() => {
                          setSelected(activity);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}

                  </div>

                  {/* Floating icon badge — sibling of the clipped image box, so its
                      overhang is not cut off. */}
                  <div className="absolute -bottom-6 left-5 z-10">
                    <div
                      className={`h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-xl ring-4 ring-card transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6 ${
                        active ? "animate-pulse-slow" : ""
                      }`}
                    >
                      <Icon className="h-7 w-7 text-primary-foreground" strokeWidth={2} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 pt-10">
                  <h3 className="text-lg font-bold leading-tight mb-1 group-hover:text-primary transition-colors duration-300">
                    {activity.title}
                  </h3>
                  <p className="text-sm font-medium text-foreground/90 leading-snug">
                    {activity.organization}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                    {activity.role}
                  </p>

                  <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-3" />

                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 min-h-[3.75rem]">
                    {activity.description}
                  </p>

                  <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground/80 font-medium">
                    <Calendar className="h-3.5 w-3.5 text-primary/70" />
                    <span>{activity.dates}</span>
                  </div>
                </div>

                {/* Hover border glow */}
                <div className="absolute inset-0 rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500 ring-1 ring-inset ring-primary/30" />
              </Card>
            );
          })}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected ? "Edit Activity" : "Add Activity"}</DialogTitle>
            <DialogDescription>
              Fill in the details for this organizational or community activity.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Image upload */}
            <div className="md:col-span-1">
              <Label>Cover Image</Label>
              <div className="mt-2">
                <div className="aspect-[16/10] rounded-lg border-2 border-dashed border-border bg-muted/30 overflow-hidden relative group">
                  {imagePreview || existingImageUrl ? (
                    <>
                      <img
                        src={imagePreview || existingImageUrl || ""}
                        alt="preview"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-destructive/90 text-destructive-foreground flex items-center justify-center hover:bg-destructive transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="h-full w-full flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors"
                    >
                      <ImageIcon className="h-8 w-8" />
                      <span className="text-xs font-medium">Click to upload</span>
                      <span className="text-[10px]">PNG, JPG up to 5MB</span>
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
                {(imagePreview || existingImageUrl) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Replace image
                  </Button>
                )}
              </div>

              <div className="mt-4">
                <Label htmlFor="org-icon">Icon (fallback)</Label>
                <Select value={iconName} onValueChange={setIconName}>
                  <SelectTrigger id="org-icon" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((name) => {
                      const Ico = ICON_MAP[name];
                      return (
                        <SelectItem key={name} value={name}>
                          <div className="flex items-center gap-2">
                            <Ico className="h-4 w-4" />
                            <span>{name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4">
                <Label htmlFor="org-sort">Sort Order</Label>
                <Input
                  id="org-sort"
                  type="number"
                  className="mt-1"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
              </div>
            </div>

            {/* Text fields */}
            <div className="md:col-span-1 space-y-3">
              <div>
                <Label htmlFor="org-title">Title</Label>
                <Input
                  id="org-title"
                  className="mt-1"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Masukkan judul"
                />
              </div>
              <div>
                <Label htmlFor="org-organization">Organization</Label>
                <Input
                  id="org-organization"
                  className="mt-1"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Masukkan nama organisasi"
                />
              </div>
              <div>
                <Label htmlFor="org-role">Role</Label>
                <Input
                  id="org-role"
                  className="mt-1"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Masukkan role"
                />
              </div>
              <div>
                <Label htmlFor="org-dates">Dates</Label>
                <Input
                  id="org-dates"
                  className="mt-1"
                  value={dates}
                  onChange={(e) => setDates(e.target.value)}
                  placeholder="Masukkan periode"
                />
              </div>
              <div>
                <Label htmlFor="org-description">Description</Label>
                <Textarea
                  id="org-description"
                  className="mt-1"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Masukkan deskripsi"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-primary to-accent text-primary-foreground"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this activity?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently remove "{selected?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default Organizational;
